/** The moving layer of the town screen: flags, smoke, clouds, birds, glints.
 *
 * `townAnim.ts` decides WHAT moves and where it is at a clock reading; this
 * file is only the clock and the drawing. Two components, one per layer, so
 * the sky's items can go under the buildings and the flags over them without
 * the painter's order knowing the life layer exists.
 *
 * The clock is a `requestAnimationFrame` loop that publishes at ~12 Hz - a
 * hand-stepped animation has nothing to show between its steps - and it does
 * not start at all when the viewer asked for reduced motion or the host has
 * animations off. Then `t` is 0 forever and every item stands at its plan
 * position on frame 0, which is the still the screenshot harness expects.
 */

import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { ScaledSprite } from "./primitives.js";
import type { LifeLayer, LifePlan } from "./townAnim.js";
import { LIFE_HD, LIFE_SPRITES, glowAlpha, poseLife } from "./townAnim.js";

const TICK_MS = 80;

/**
 * Milliseconds since the screen mounted, or 0 while animation is off.
 *
 * `enabled` is the host's switch; the media query is the viewer's. Either one
 * off freezes the clock at zero rather than pausing it mid-wave, so a frozen
 * town always shows the same frame.
 */
export function useLifeClock(enabled: boolean): number {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (!enabled) { setT(0); return; }
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setT(0); return; }
    let raf = 0;
    let last = 0;
    const start = performance.now();
    const step = (now: number): void => {
      if (now - last >= TICK_MS) {
        last = now;
        setT(Math.floor(now - start));
      }
      raf = window.requestAnimationFrame(step);
    };
    raf = window.requestAnimationFrame(step);
    return () => { window.cancelAnimationFrame(raf); };
  }, [enabled]);
  return t;
}

/** One layer of the life plan, posed at `t`. */
export function TownLife(props: {
  readonly plan: LifePlan;
  readonly layer: LifeLayer;
  readonly t: number;
}): JSX.Element {
  const { plan, layer, t } = props;
  const poses = useMemo(
    () => poseLife(plan, t).filter((p) => p.item.layer === layer),
    [plan, t, layer],
  );
  return (
    <div className="hs-town__life" aria-hidden="true">
      {layer === "sky" ? plan.glows.map((g, i) => (
        <div
          key={`g${String(i)}`}
          className="hs-town__life-glow"
          style={{
            left: `${String(Math.round(g.x - g.rx))}px`,
            top: `${String(Math.round(g.y - g.ry))}px`,
            width: `${String(g.rx * 2)}px`,
            height: `${String(g.ry * 2)}px`,
            opacity: glowAlpha(g, t).toFixed(3),
            backgroundImage: `radial-gradient(ellipse at center, rgba(${g.rgb},0.9) 0%, rgba(${g.rgb},0.45) 40%, rgba(${g.rgb},0) 70%)`,
          } as CSSProperties}
        />
      )) : null}
      {poses.map((p, i) => {
        const s = LIFE_SPRITES[p.item.sprite];
        if (s === undefined) return null;
        // The frame ships at LIFE_HD times its canvas size (gen_life.py) and
        // is drawn at 1x in a box of its own size, scaled down about its
        // top-left by 1/LIFE_HD and up by the item's magnification - the
        // same contract as the building sprites, so a flag over a hi-def
        // keep is hi-def cloth rather than a pixel stamp.
        const k = p.item.scale / LIFE_HD;
        return (
          <span
            key={`${p.item.sprite}:${String(i)}`}
            className="hs-town__life-item"
            style={{
              left: `${String(p.x)}px`,
              top: `${String(p.y)}px`,
              width: `${String(Math.round(s.w * LIFE_HD))}px`,
              height: `${String(Math.round(s.h * LIFE_HD))}px`,
              opacity: p.opacity,
              transformOrigin: "0 0",
              transform: `scale(${k.toFixed(4)})${p.item.flip === true ? " scaleX(-1)" : ""}`,
            }}
          >
            <ScaledSprite atlasKey={p.key} scale={1} className="hs-town__life-art" />
          </span>
        );
      })}
    </div>
  );
}

/** The layer's own rules, mounted by TownScreen beside its paint styles. */
export const LIFE_CSS = `
.hs-town .hs-town__life { position: absolute; inset: 0; pointer-events: none; }
.hs-town .hs-town__life-item { position: absolute; display: block; image-rendering: auto; }
.hs-town .hs-town__life-art { display: block; image-rendering: auto; }
.hs-town .hs-town__life-item .hm-icon { image-rendering: auto; }
.hs-town .hs-town__life-glow { position: absolute; pointer-events: none; mix-blend-mode: screen; }
`;
